/**
 * Privacy Policy
 *
 * Public page — no auth required.
 */

export function Privacy() {
	return (
		<div class="page legal-page">
			<header class="legal-header">
				<div class="container">
					<nav class="legal-nav">
						<a href="/" class="logo">
							<span class="logo-text">pxtxt</span>
						</a>
					</nav>
				</div>
			</header>

			<main class="legal-main">
				<div class="container legal-container">
					<h1>Privacy Policy</h1>
					<p class="legal-meta">Last updated: March 28, 2026</p>

					<section>
						<h2>1. Information We Collect</h2>

						<h3>Account Information</h3>
						<p>
							When you create an account, we collect your email address and display name
							through our authentication provider (Clerk). We assign you a user identifier
							for service operations.
						</p>

						<h3>Professional Information</h3>
						<p>
							Through the interview process, you provide professional information including
							work history, skills, projects, achievements, and voice/tone preferences.
							This information is structured through our interview agent and stored as
							canonical data in your profile.
						</p>

						<h3>Usage Data</h3>
						<p>
							We track interview session counts and timestamps for tier limit enforcement
							and service improvement. We do not use third-party analytics or tracking services.
						</p>

						<h3>Payment Information</h3>
						<p>
							Payment processing is handled entirely by Stripe. We store only your Stripe
							customer identifier to manage your subscription. We never see or store your
							credit card number, expiration date, or CVC.
						</p>

						<h3>Engagement Data</h3>
						<p>
							When visitors interact with your representation agent, conversation data is
							stored temporarily in our system with a 7-day time-to-live. This data is
							automatically deleted after 7 days.
						</p>
					</section>

					<section>
						<h2>2. How We Use Your Information</h2>
						<ul>
							<li><strong>Service delivery</strong>: Building professional narratives, powering representation agents, displaying public profiles</li>
							<li><strong>AI processing</strong>: Interview conversations and representation responses are processed by Anthropic's Claude API</li>
							<li><strong>Company enrichment</strong>: Company names from your profile may be sent to BETA (operated by CaliKo LLC) for contextual enrichment</li>
							<li><strong>Authentication</strong>: Account management through Clerk</li>
							<li><strong>Payment processing</strong>: Subscription management through Stripe</li>
						</ul>
					</section>

					<section>
						<h2>3. Data Sensitivity</h2>
						<p>
							We classify your data by sensitivity to apply appropriate protections:
						</p>
						<ul>
							<li>
								<strong>Standard sensitivity</strong>: Professional title, skills, company names, project descriptions,
								voice preferences. This information is often publicly available (e.g., on LinkedIn) and is used for
								your public profile and representation.
							</li>
							<li>
								<strong>Higher sensitivity</strong>: Career gaps, salary expectations, deal-breakers,
								topics you've marked as off-limits (guardrails), and raw interview transcripts.
								This data is never shared with third parties beyond AI processing and is not
								included in public profiles or representation responses.
							</li>
						</ul>
					</section>

					<section>
						<h2>4. Third-Party Services</h2>
						<p>We use the following third-party services to operate pxtxt:</p>
						<ul>
							<li>
								<strong>Anthropic (Claude API)</strong>: Processes interview conversations and generates
								representation responses. Anthropic does not train on data submitted through their API.
								<a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener">Anthropic Privacy Policy</a>
							</li>
							<li>
								<strong>BETA (CaliKo LLC)</strong>: Company names and tenure dates may be shared for
								enrichment with publicly available company context. No personal information is shared with BETA.
							</li>
							<li>
								<strong>Clerk</strong>: Handles authentication, including email, password hashing, and session management.
								<a href="https://clerk.com/privacy" target="_blank" rel="noopener">Clerk Privacy Policy</a>
							</li>
							<li>
								<strong>Stripe</strong>: Processes all payment information. We never handle your payment details directly.
								<a href="https://stripe.com/privacy" target="_blank" rel="noopener">Stripe Privacy Policy</a>
							</li>
							<li>
								<strong>Cloudflare</strong>: Provides infrastructure including compute (Workers), database (D1),
								and key-value storage (KV).
								<a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener">Cloudflare Privacy Policy</a>
							</li>
						</ul>
					</section>

					<section>
						<h2>5. Data Storage and Retention</h2>
						<ul>
							<li><strong>Canonical data</strong>: Stored in Cloudflare D1 database. Retained while your account is active.</li>
							<li><strong>Interview transcripts</strong>: Stored in D1. Retained while your account is active.</li>
							<li><strong>Engagement data</strong>: Stored in Cloudflare KV with a 7-day TTL. Automatically deleted after 7 days.</li>
							<li><strong>Account data</strong>: Retained until you delete your account.</li>
							<li><strong>After deletion</strong>: All data is permanently removed within 30 days of account deletion.</li>
						</ul>
					</section>

					<section>
						<h2>6. Data Portability</h2>
						<p>
							You can export your full canonical data in JSON format at any time through the Service.
							This export includes all approved claims, canonical snapshots, and your professional narrative.
							Your data is yours — you can take it with you if you leave.
						</p>
					</section>

					<section>
						<h2>7. Data Deletion</h2>
						<p>
							You can request deletion of your account and all associated data at any time.
							Upon deletion:
						</p>
						<ul>
							<li>Your profile, canonical data, interview transcripts, and claims are permanently deleted</li>
							<li>Your public profile and representation agent are immediately deactivated</li>
							<li>Engagement data (visitor conversations) auto-deletes via 7-day KV TTL</li>
							<li>Deletion is completed within 30 days of your request</li>
						</ul>
					</section>

					<section>
						<h2>8. Security</h2>
						<p>We protect your data through:</p>
						<ul>
							<li><strong>Edge compute</strong>: Cloudflare Workers execute at the network edge with no persistent server access</li>
							<li><strong>Encryption in transit</strong>: All data transmitted over HTTPS/TLS</li>
							<li><strong>Encryption at rest</strong>: D1 database encryption managed by Cloudflare</li>
							<li><strong>Secret management</strong>: API keys stored as encrypted secrets in Cloudflare Workers</li>
							<li><strong>Authentication</strong>: JWT-based authentication through Clerk with RS256 signature verification</li>
							<li><strong>Data isolation</strong>: Each user's data is isolated — no cross-user data access is possible</li>
						</ul>
					</section>

					<section>
						<h2>9. Cookies</h2>
						<p>
							We use only essential cookies required for authentication (Clerk session cookies).
							We do not use advertising cookies, analytics cookies, or third-party tracking cookies.
						</p>
					</section>

					<section>
						<h2>10. Children's Privacy</h2>
						<p>
							The Service is not intended for individuals under 18 years of age.
							We do not knowingly collect personal information from minors.
						</p>
					</section>

					<section>
						<h2>11. Your Rights</h2>
						<p>Depending on your jurisdiction, you may have the following rights:</p>
						<ul>
							<li><strong>Access</strong>: View your data through the Service dashboard and profile</li>
							<li><strong>Portability</strong>: Export your canonical data in JSON format</li>
							<li><strong>Rectification</strong>: Edit your claims, update your profile, re-interview to correct information</li>
							<li><strong>Erasure</strong>: Delete your account and all associated data</li>
							<li><strong>Objection</strong>: Unpublish your profile to stop public representation at any time</li>
						</ul>
						<p>
							We process your data on the basis of your consent (account creation) and
							legitimate interest (service delivery and improvement).
						</p>
					</section>

					<section>
						<h2>12. Changes to This Policy</h2>
						<p>
							We may update this privacy policy from time to time. Material changes will be communicated
							via email with at least 30 days' notice. The "Last updated" date at the top of this page
							indicates when the policy was most recently revised.
						</p>
					</section>

					<section>
						<h2>13. Contact</h2>
						<p>
							For privacy-related questions or to exercise your data rights,
							contact us at <a href="mailto:privacy@pxtxt.ai">privacy@pxtxt.ai</a>.
						</p>
					</section>
				</div>
			</main>

			<Footer />
			<style>{legalStyles}</style>
		</div>
	);
}

function Footer() {
	return (
		<footer class="legal-footer">
			<div class="container">
				<div class="footer-content">
					<p class="t-caption">pxtxt — Professional representation with depth</p>
					<nav class="footer-links">
						<a href="/terms">Terms</a>
						<a href="/privacy">Privacy</a>
					</nav>
				</div>
			</div>
		</footer>
	);
}

const legalStyles = `
	.legal-page {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		background: var(--color-bg);
	}

	.legal-header {
		padding: 1rem 0;
		background: var(--color-bg-elevated);
		border-bottom: 1px solid var(--color-border);
	}

	.legal-nav {
		display: flex;
		align-items: center;
	}

	.logo {
		text-decoration: none;
	}

	.logo-text {
		font-family: var(--font-display);
		font-size: 1.5rem;
		font-weight: 700;
		color: var(--color-title);
		letter-spacing: -0.02em;
	}

	.legal-main {
		flex: 1;
		padding: 3rem 0;
	}

	.legal-container {
		max-width: 720px;
		margin: 0 auto;
		padding: 0 1.5rem;
	}

	.legal-container h1 {
		font-family: var(--font-display);
		font-size: 2rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.5rem;
	}

	.legal-meta {
		color: var(--color-text-muted);
		font-size: 0.875rem;
		margin-bottom: 2.5rem;
	}

	.legal-container section {
		margin-bottom: 2rem;
	}

	.legal-container h2 {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.legal-container h3 {
		font-family: var(--font-display);
		font-size: 1.0625rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 1rem 0 0.5rem;
	}

	.legal-container p {
		line-height: 1.7;
		color: var(--color-text);
		margin: 0 0 0.75rem;
	}

	.legal-container ul {
		padding-left: 1.5rem;
		margin: 0 0 0.75rem;
	}

	.legal-container li {
		line-height: 1.7;
		color: var(--color-text);
		margin-bottom: 0.375rem;
	}

	.legal-container a {
		color: var(--color-primary);
		text-decoration: none;
	}

	.legal-container a:hover {
		text-decoration: underline;
	}

	.legal-footer {
		padding: 2rem 0;
		border-top: 1px solid var(--color-border);
	}

	.footer-content {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.footer-links {
		display: flex;
		gap: 1.5rem;
	}

	.footer-links a {
		color: var(--color-text-muted);
		text-decoration: none;
		font-size: 0.875rem;
	}

	.footer-links a:hover {
		color: var(--color-text);
	}

	@media (max-width: 767px) {
		.legal-main {
			padding: 2rem 0;
		}

		.legal-container h1 {
			font-size: 1.5rem;
		}

		.footer-content {
			flex-direction: column;
			gap: 0.75rem;
			text-align: center;
		}
	}
`;
